import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import { Form, Formik } from "formik";
import { Fragment, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as yup from "yup";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import useStaticIPs from "../../../../api/hooks/useStaticIPs";
import { integrationKeys } from "../../../../api/queryKeys";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import CustomCopyButton from "../../../../components/CustomCopyButton";
import ExternalLink from "../../../../components/ExternalLink";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import { logos } from "../../environment/icons/iconExport";
import BreezeStaticIPsModal from "../components/BreezeStaticIPsModal";
import Guideline from "../components/Guideline";
import GuidelineInstruction from "../components/GuidelineInstruction";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import cloudconnexa_open_vpn_step_1 from "./images/cloudconnexa_vpn_step_1.png";
import cloudconnexa_open_vpn_step_2 from "./images/cloudconnexa_vpn_step_2.png";
import cloudconnexa_open_vpn_step_3 from "./images/cloudconnexa_vpn_step_3.png";

const CloudConnexaOpenVPNIntegration = () => {
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated(
    "cloudconnexa_open_vpn"
  );
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [showStaticIPsModal, setShowStaticIPsModal] = useState(false);
  const handleClickShowSecret = () => setShowSecret(!showSecret);
  const handleMouseDownSecret = () => setShowSecret(!showSecret);
  const { registerCloudConnexaOpenVPNIntegration } = useAPI();
  const queryClient = useQueryClient();
  const { staticIPs: allowlistIps } = useStaticIPs();

  const formInitialValues = {
    clientId: "",
    clientSecret: "",
    tenant: "",
  };
  const formCheckoutSchema = yup.object().shape({
    clientId: yup.string().required("required"),
    clientSecret: yup.string().required("required"),
    tenant: yup.string().required("required"),
  });
  const handleFormSubmit = (values) => {
    onSubmit(values);
  };

  const sanitizeTenantInput = (tenant) => {
    if (!tenant) return "";

    const cleanTenant = tenant.trim();

    if (cleanTenant.includes("://") || cleanTenant.includes(".openvpn.com")) {
      try {
        if (cleanTenant.includes("://")) {
          const url = new URL(cleanTenant);
          const match = url.hostname.match(/^(.*?)\.(?:api\.)?openvpn\.com$/);
          return match ? match[1] : cleanTenant;
        }

        if (cleanTenant.includes(".openvpn.com")) {
          const withoutProtocol = cleanTenant.replace(/^https?:\/\//, "");
          const match = withoutProtocol.match(/^(.*?)\.(?:api\.)?openvpn\.com/);
          return match ? match[1] : cleanTenant;
        }
      } catch (error) {
        Toast.error("Invalid tenant URL");
      }
    }

    return cleanTenant
      .replace(/^https?:\/\//, "")
      .replace(/\.(?:api\.)?openvpn\.com.*$/, "")
      .split(".")[0];
  };

  const onSubmit = (values) => {
    setLoading(true);
    const tenantName = sanitizeTenantInput(values.tenant);
    const baseUrl = `https://${tenantName}.api.openvpn.com`;
    registerCloudConnexaOpenVPNIntegration(
      values.clientId,
      values.clientSecret,
      baseUrl
    )
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("CloudConnexa OpenVPN integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);

  if (loading || isIntegratedLoading) {
    return <LoadingIcon />;
  }

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated
            ? "Once integrated, Breeze will scan your CloudConnexa OpenVPN for misconfigurations."
            : ""
        }
        image={logos.OpenVPNLogo}
        name={"CloudConnexa OpenVPN"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Fragment>
              <Typography variant={"h4"}>
                CloudConnexa OpenVPN is integrated!
              </Typography>
            </Fragment>
          ) : (
            <>
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton
                      onClick={() => setShowStaticIPsModal(true)}
                    >
                      View Breeze Static IPs
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Formik
                  onSubmit={handleFormSubmit}
                  initialValues={formInitialValues}
                  validationSchema={formCheckoutSchema}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <GuidelineStepByStep>
                        <GuidelineStep title="Login into your CloudConnexa OpenVPN account">
                          <ExternalLink
                            href={`https://myaccount.openvpn.com/signin/cvpn/previous-credentials`}
                            text="Follow the URL in your browser to login "
                            linkText="CloudConnexa OpenVPN account as
                            administrator"
                          />
                        </GuidelineStep>

                        <GuidelineStep title="Generate the API Credentials">
                          <Typography variant={"body2"}>
                            Go to API & Logs {"->"} API
                          </Typography>
                          <Typography variant={"body2"}>
                            Toggle the "Enable API for this Cloud ID" option and
                            click on Create Credentials.
                          </Typography>
                          <ScreenshotAccordion
                            images={[
                              {
                                file: cloudconnexa_open_vpn_step_1,
                                altext: "create credentials",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                          <GuidelineInstruction
                            text={
                              <span>
                                Optional - Add the following IP addresses to the
                                allowlist:{" "}
                                <strong>
                                  {allowlistIps.map((ip, index) => (
                                    <Box
                                      key={index}
                                      display="flex"
                                      alignItems="center"
                                      gap={1}
                                      sx={{
                                        margin: 0,
                                        padding: "4px 0",
                                      }}
                                    >
                                      <span>{ip}</span>
                                      <CustomCopyButton
                                        copyTitle={"IP Address"}
                                        copyText={ip}
                                      />
                                    </Box>
                                  ))}
                                </strong>
                              </span>
                            }
                          />
                          <Typography variant={"body2"}>
                            Fill in the following details, then click Create.
                          </Typography>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant={"body2"}>
                              Name: <strong>Breeze Security</strong>
                            </Typography>
                            <CustomCopyButton
                              copyTitle={"name"}
                              copyText={"Breeze Security"}
                            />
                          </Box>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant={"body2"}>
                              Lifetime: <strong>1 year</strong>
                            </Typography>
                          </Box>

                          <ScreenshotAccordion
                            images={[
                              {
                                file: cloudconnexa_open_vpn_step_2,
                                altext: "create credentials",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                          <Typography variant={"body2"}>
                            Copy the Client ID and Client Secret and paste them
                            below.
                          </Typography>
                          <ScreenshotAccordion
                            images={[
                              {
                                file: cloudconnexa_open_vpn_step_3,
                                altext: "paste credentials",
                              },
                            ]}
                            onExpanded={addToExpandedList}
                          />
                        </GuidelineStep>
                        <GuidelineStep title="Paste the Credentials and console URL">
                          <Box width={700}>
                            <Box display="grid" mb="20px" gap="10px">
                              <TextField
                                fullWidth
                                variant="filled"
                                name="clientId"
                                autoComplete="off"
                                label="Client ID"
                                value={values.clientId}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={!!touched.clientId && !!errors.clientId}
                                helperText={touched.clientId && errors.clientId}
                                disabled={guideMode}
                              />
                            </Box>
                            <Box display="grid" mb="20px" gap="10px">
                              <TextField
                                fullWidth
                                variant="filled"
                                type={showSecret ? "text" : "password"}
                                name="clientSecret"
                                autoComplete="off"
                                label="Client Secret"
                                value={values.clientSecret}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={
                                  !!touched.clientSecret &&
                                  !!errors.clientSecret
                                }
                                helperText={
                                  touched.clientSecret && errors.clientSecret
                                }
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        aria-label="toggle secret visibility"
                                        onClick={handleClickShowSecret}
                                        onMouseDown={handleMouseDownSecret}
                                      >
                                        {showSecret ? (
                                          <Visibility />
                                        ) : (
                                          <VisibilityOff />
                                        )}
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                }}
                                disabled={guideMode}
                              />
                            </Box>

                            <Grid item>
                              <TextField
                                fullWidth
                                variant="filled"
                                name="tenant"
                                label="console URL - e.g. https://tenant-url.openvpn.com"
                                value={values.tenant}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={!!touched.tenant && !!errors.tenant}
                                helperText={touched.tenant && errors.tenant}
                                disabled={guideMode}
                              />
                            </Grid>
                          </Box>
                          <Box display="flex" justifyContent="center">
                            <PrimaryButton type="submit" disabled={guideMode}>
                              Connect
                            </PrimaryButton>
                          </Box>
                        </GuidelineStep>
                      </GuidelineStepByStep>
                    </Form>
                  )}
                </Formik>
              </Box>
            </>
          )
        }
      />
      <BreezeStaticIPsModal
        open={showStaticIPsModal}
        onClose={() => setShowStaticIPsModal(false)}
      />
    </Box>
  );
};

export default CloudConnexaOpenVPNIntegration;
